import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { magicQuoteApi } from '../api/endpoints.ts';
import { flush, reassignSession } from './capture-queue.ts';
import { isLocalWalkthroughId, newLocalWalkthroughId, noteAdoption } from './walkthrough-id.ts';

/**
 * Walkthroughs that were started with no signal.
 *
 * The capture queue solved half of the offline problem years before this file
 * existed: once a walkthrough is under way, every photo, clip and note is
 * written to IndexedDB before it is ever offered to the server, so the walk
 * itself has never needed a network. The half it did not solve was the first
 * step. Starting a walkthrough meant `POST /magic-quotes`, because the id
 * everything hangs off was minted by that INSERT — so a cleaner already
 * standing in the house, in the exact conditions the rest of the feature was
 * designed around, could not begin.
 *
 * So the device mints the id instead, and the walkthrough lives here until
 * there is a network to hand it over on. What that costs is one screen's worth
 * of honesty — a walkthrough waiting here is not on the server, cannot be
 * organized, and is on this browser only — which the home screen says plainly
 * rather than implying otherwise with a row that looks like all the others.
 *
 * What it does NOT cost is the ordering guarantee the whole feature rests on.
 * Captures stay keyed by the local id and stay in the queue until the handover
 * re-keys them in one transaction, so the walk reaches the server in the order
 * it was walked — room marker first, then the six photos it names.
 */

const DB_NAME = 'magic-quote-walkthroughs';
const DB_VERSION = 1;
const STORE = 'walkthroughs';

/**
 * How long an already-handed-over record is kept.
 *
 * It stays only to answer one question — "the tab is still open at
 * /magic-quote/local-abc, where did that go?" — and a fortnight is far longer
 * than any tab survives. The rows are a few hundred bytes; the photos they
 * used to point at left with the handover.
 */
const ADOPTED_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export interface LocalWalkthrough {
  /** The device-minted id. Primary key, route parameter, and dedupe key. */
  localId: string;
  /**
   * The company whose walkthrough this is.
   *
   * A browser is not one company's. Somebody walks a property for their own
   * company, comes back with no signal, signs out, and a colleague signs in
   * to a different tenant on the same laptop — and without this the handover
   * would post the first company's walkthrough under the second company's
   * session and upload the interior photographs of a stranger's home into
   * their tenant. Adoption therefore refuses anything that does not name the
   * company currently signed in, and waits instead.
   *
   * Null only for a record written before this field existed, which is
   * treated the same way as a mismatch: not adopted, and not thrown away
   * either — it is still somebody's walkthrough.
   */
  companyId: string | null;
  /**
   * The location the walk was made in.
   *
   * A walkthrough belongs to a branch, not just a company, and every route
   * that touches one afterwards enforces that: `requireSessionRow` answers 404
   * for another location's walkthrough. Handing this over while signed into a
   * different branch would therefore succeed — the dedupe lookup is
   * company-wide, as it must be, or a retry from the wrong branch would make a
   * second walkthrough — and then every capture upload would 404, which the
   * queue reads as a refusal and abandons. Photographs of a customer's home,
   * dropped for being in the wrong branch. So the check is made here, before
   * anything is sent, and the walkthrough simply waits for its own branch.
   */
  locationId: string | null;
  customerId: string | null;
  /**
   * Written down at the doorstep, not looked up later.
   *
   * The name comes from a customer list that is itself a network read, so a
   * walkthrough started offline for a known customer would otherwise show a
   * blank where the name goes — or, worse, "No customer yet" for one that has
   * a customer perfectly well.
   */
  customerName: string | null;
  propertyId: string | null;
  propertyAddress: string | null;
  mode: 'quick' | 'guided' | 'free';
  startedAt: string;
  /** The server's id, once it has one. Null while the walkthrough is local. */
  serverId: string | null;
  adoptedAt: string | null;
  /**
   * Why the last handover attempt was refused, when it was refused on its
   * merits rather than for want of a network.
   *
   * A company that switched Magic Quote off, or dropped to a plan without it,
   * while somebody was mid-walk is the case this exists for: retrying forever
   * would be silent and hopeless, so the reason is kept and shown, and the
   * captures stay on the device where the estimator can still see them.
   */
  lastError: string | null;
}

interface LocalWalkthroughSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: LocalWalkthrough;
    indexes: { 'by-started': string };
  };
}

let dbPromise: Promise<IDBPDatabase<LocalWalkthroughSchema>> | null = null;

function db(): Promise<IDBPDatabase<LocalWalkthroughSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<LocalWalkthroughSchema>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const store = database.createObjectStore(STORE, { keyPath: 'localId' });
        store.createIndex('by-started', 'startedAt');
      },
      terminated() {
        dbPromise = null;
      },
    });
    // A rejected open must not be remembered as the answer — the same lesson
    // the capture queue learned, and for the same reason: one bad moment in a
    // private window would otherwise be permanent for the life of the page.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

async function safely<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}

/**
 * Starts a walkthrough on this device.
 *
 * Returns null when there is nowhere to write it. That is not a detail the
 * caller may skip: without a record, the walkthrough screen has no customer,
 * no address and no way to list itself afterwards, and a walk captured against
 * an id nothing remembers is a walk nobody can find. The home screen says so
 * and stays where it is rather than opening a camera onto a dead end.
 */
export async function startLocalWalkthrough(input: {
  /**
   * The id to file it under, when the caller has already minted one.
   *
   * The online Start path mints its id BEFORE the request and sends it as
   * `clientSessionId`, so that a request which reached the server but whose
   * reply was lost is the same walkthrough as the local one this then writes.
   * Without that, the fallback would mint a second id, adoption would post it,
   * and the company would own two walkthroughs of one house — one of them
   * holding every photo and the other holding none.
   */
  localId?: string;
  companyId?: string | null;
  locationId?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  propertyId?: string | null;
  propertyAddress?: string | null;
  mode?: 'quick' | 'guided' | 'free';
}): Promise<LocalWalkthrough | null> {
  const record: LocalWalkthrough = {
    localId: input.localId ?? newLocalWalkthroughId(),
    companyId: input.companyId ?? null,
    locationId: input.locationId ?? null,
    customerId: input.customerId ?? null,
    customerName: input.customerName ?? null,
    propertyId: input.propertyId ?? null,
    propertyAddress: input.propertyAddress ?? null,
    mode: input.mode ?? 'free',
    startedAt: new Date().toISOString(),
    serverId: null,
    adoptedAt: null,
    lastError: null,
  };
  return safely(async () => {
    const database = await db();
    await database.put(STORE, record);
    return record;
  }, null);
}

/**
 * One walkthrough, if it is this company's to open.
 *
 * The id is in the URL, so this is reachable by typing — and by a colleague
 * at another company who signed in on the same browser. A walkthrough that is
 * not theirs reads as one that is not here.
 */
export async function findLocalWalkthrough(
  localId: string,
  scope: { companyId: string | null; locationId: string | null },
): Promise<LocalWalkthrough | null> {
  return safely(async () => {
    const row = await (await db()).get(STORE, localId);
    if (!row || !inScope(row, scope)) return null;
    return row;
  }, null);
}

/**
 * Whether this walkthrough is one the current session may see.
 *
 * Company and branch, because a walkthrough on the server is scoped to both
 * and what may be seen of one here is scoped the same way: a shared browser
 * moved between branches should not put one branch's customer names in front
 * of the other's estimators. A record naming no branch predates the field and
 * is shown to its company, which is where it already was.
 */
function inScope(row: LocalWalkthrough, scope: { companyId: string | null; locationId: string | null }): boolean {
  if (!scope.companyId || row.companyId !== scope.companyId) return false;
  return !row.locationId || !scope.locationId || row.locationId === scope.locationId;
}

/**
 * Every walkthrough this company still has waiting here, newest first.
 *
 * Scoped for the same reason adoption is, and it is the same browser that
 * makes it necessary: sign out, hand the laptop to a colleague at another
 * company, and an unscoped list would show them the customer's name on a walk
 * that is none of their business — and let them open it and look at the
 * photographs queued under it. A record belonging to somebody else is not
 * listed and not deleted; it waits for its own company to come back.
 */
export async function listLocalWalkthroughs(scope: {
  companyId: string | null;
  locationId: string | null;
}): Promise<LocalWalkthrough[]> {
  if (!scope.companyId) return [];
  return safely(async () => {
    const database = await db();
    const rows = await database.getAllFromIndex(STORE, 'by-started');
    return rows.filter((row) => row.serverId === null && inScope(row, scope)).reverse();
  }, []);
}

/**
 * Forgets a walkthrough outright — the local half of deleting one.
 *
 * Waits for any handover in flight first, and that wait is the point. Delete
 * and adopt race over the same record: the handover can create the server
 * walkthrough, re-key the captures and write its own copy of the record back
 * either side of these deletes, so a walkthrough somebody explicitly removed
 * could reappear in the list — or, worse, be uploaded to the server after the
 * confirmation said it was gone. Afterwards the record is re-read: if the
 * handover did land, the caller is told the server id so it can delete the
 * walkthrough where it now lives.
 */
export async function removeLocalWalkthrough(localId: string): Promise<{ serverId: string | null }> {
  await adoptionSettled();
  const record = await safely(async () => (await db()).get(STORE, localId), undefined);
  await safely(async () => {
    const database = await db();
    await database.delete(STORE, localId);
  }, undefined);
  return { serverId: record?.serverId ?? null };
}

/** Resolves once no handover is in flight. */
export async function adoptionSettled(): Promise<void> {
  await adopting?.catch(() => undefined);
}

/**
 * Drops handed-over records once nothing could still be looking for them.
 *
 * Called at startup beside the quote drafts' own housekeeping, and only ever
 * removes rows whose walkthrough is safely on the server.
 */
export async function pruneAdoptedWalkthroughs(now = Date.now()): Promise<void> {
  await safely(async () => {
    const database = await db();
    for (const row of await database.getAll(STORE)) {
      if (!row.adoptedAt) continue;
      if (now - new Date(row.adoptedAt).getTime() > ADOPTED_TTL_MS) await database.delete(STORE, row.localId);
    }
  }, undefined);
}

export interface AdoptionResult {
  localId: string;
  serverId: string | null;
  /** Captures moved onto the server's id. */
  moved: number;
  error: string | null;
}

/**
 * The handover already in flight, so two triggers cannot start two.
 *
 * Both triggers are ordinary — the app mounts, and the browser fires `online`
 * a moment later — and two passes over the same records would each post the
 * same walkthrough. The server's dedupe key makes that harmless rather than
 * duplicating anything, but "harmless" here still means two round trips on the
 * connection that just came back, and two re-keys racing over one queue.
 */
let adopting: Promise<AdoptionResult[]> | null = null;

/**
 * Hands every waiting walkthrough to the server, then drains its captures.
 *
 * The order matters and is the whole reason this is one function: the
 * walkthrough has to exist server-side before a single capture can be
 * uploaded against it, and the captures have to be re-keyed before the drain
 * can find them. Doing any of it out of order fails in a way that looks like
 * the network — the case this code is on the wrong side of by definition.
 */
export async function adoptLocalWalkthroughs(scope: {
  companyId: string | null;
  locationId: string | null;
  /**
   * Whether the session this handover belongs to is still the session in
   * force — checked again before every request, not just at the start.
   *
   * A handover walks a list, posts, re-keys and flushes, and on a slow
   * connection that takes long enough for somebody to sign out, sign in
   * elsewhere or switch branch in the middle of it. Every call after that
   * point carries the NEW cookie while the records were chosen for the old
   * one: one tenant's walkthrough posted into another's, or a walkthrough
   * marked adopted whose captures then 404 for being in the wrong branch.
   */
  isCurrent?: () => boolean;
  /**
   * Asked once, before the first request that creates a walkthrough, and only
   * when one is actually waiting.
   *
   * Creating the walkthrough is a Magic Quote post, and that post is refused
   * until the AI notice is accepted. The capture queue never uploads a local
   * id, so this handover is the request that would otherwise 403 forever with
   * no dialog. A rejection — declined, or the notice could not be loaded —
   * leaves the walks on the device. It is not a refusal of any one of them.
   */
  beforeRequest?: () => Promise<void>;
}): Promise<AdoptionResult[]> {
  if (adopting) return adopting;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return [];
  const { companyId, locationId } = scope;
  // Nobody is signed in yet, so there is no tenant to hand anything to. The
  // records wait; they are not anybody's to give away in the meantime.
  if (!companyId) return [];

  adopting = (async () => {
    const results: AdoptionResult[] = [];
    // Already scoped to this company and branch by the list itself: a walk
    // made in another branch is not offered here, and handing it over from
    // this one would return that branch's walkthrough and then 404 every
    // capture uploaded against it.
    const records = await listLocalWalkthroughs({ companyId, locationId });
    // Nothing to send means nothing to consent to. Opening the notice on an
    // empty store would ask on every page, every minute, for no request.
    const waiting = records.some((record) => !record.lastError);
    if (!waiting) return results;
    if (scope.isCurrent && !scope.isCurrent()) return results;
    if (scope.beforeRequest) {
      try {
        await scope.beforeRequest();
      } catch {
        return results;
      }
    }
    if (scope.isCurrent && !scope.isCurrent()) return results;
    for (const record of records) {
      // The session moved out from under this pass. Stopping is the only safe
      // answer — everything below would run against somebody else's cookie.
      if (scope.isCurrent && !scope.isCurrent()) break;
      // Already refused on its merits. Asking again every sixty seconds would
      // be a request a minute for a walkthrough the server has said no to, and
      // the answer would not change. The reason stays on the row where the
      // estimator can read it, and the captures stay on the device.
      if (record.lastError) continue;
      try {
        const session = await magicQuoteApi.start({
          customerId: record.customerId,
          propertyId: record.propertyId,
          mode: record.mode,
          // What makes this safe to repeat. A phone that posted this
          // walkthrough and lost the reply will post it again on the next
          // trigger, and get the same walkthrough back rather than a second
          // one holding half the house.
          clientSessionId: record.localId,
        });

        // Before a single row moves, and that ordering is the whole guarantee.
        // The walkthrough screen is still open, still says `local-…`, and the
        // cleaner is still taking photographs; from this line on, every one of
        // them is written under the server's id instead. What is left under
        // the old id is therefore exactly what the sweep below moves, with
        // nothing arriving behind it.
        noteAdoption(record.localId, session.id);
        const moved = await reassignSession(record.localId, session.id);
        // The store refused to move them. Marking the walkthrough adopted now
        // would take it off the pending list while its captures sat under an
        // id nothing drains — the walk finishing on the server without the
        // rooms it was made of. Left as it is, the next pass posts the same
        // id (which returns the same walkthrough) and sweeps again.
        if (moved === null) {
          results.push({ localId: record.localId, serverId: null, moved: 0, error: null });
          break;
        }
        await safely(async () => {
          const database = await db();
          await database.put(STORE, {
            ...record,
            serverId: session.id,
            adoptedAt: new Date().toISOString(),
            lastError: null,
          });
        }, undefined);

        // Straight away, rather than leaving it to the eight-second timer on a
        // screen that may not even be open: the connection is up right now,
        // and the estimator is probably watching a counter that says how many
        // captures are still on their phone.
        await flush(session.id);
        results.push({ localId: record.localId, serverId: session.id, moved, error: null });
      } catch (error) {
        /**
         * A customer or property that went away while this device was offline.
         *
         * The walkthrough was never on the server, so nothing stopped a
         * colleague deleting the customer it names — and the handover then
         * fails with a 404 about a record this walk is only loosely attached
         * to. Giving up there would strand every photograph of the property
         * over a name, which is backwards: a walkthrough with no customer is
         * an ordinary, supported thing — it is how every doorstep walk starts
         * — and the review screen exists to settle who it is for afterwards.
         * So the association is dropped and the walk is handed over
         * unidentified on the next pass.
         */
        if (isMissingReference(error) && (record.customerId || record.propertyId)) {
          await safely(async () => {
            const database = await db();
            await database.put(STORE, { ...record, customerId: null, propertyId: null });
          }, undefined);
          results.push({ localId: record.localId, serverId: null, moved: 0, error: null });
          continue;
        }
        const permanent = isRefusal(error);
        const message = error instanceof Error ? error.message : 'That walkthrough could not be sent.';
        if (permanent) {
          await safely(async () => {
            const database = await db();
            await database.put(STORE, { ...record, lastError: message });
          }, undefined);
        }
        results.push({ localId: record.localId, serverId: null, moved: 0, error: permanent ? message : null });
        // A refusal is about this walkthrough; a network failure is about the
        // network, and the ones behind it would fail the same way. Stopping
        // also keeps the walks in the order they were made.
        if (!permanent) break;
      }
    }
    return results;
  })();

  try {
    return await adopting;
  } finally {
    adopting = null;
  }
}

/**
 * Whether the server decided about this walkthrough rather than failed to hear
 * it — the same distinction the capture queue draws, and drawn the same way.
 *
 * 401 and 403 are excluded for the reason they are excluded there: they are
 * usually a session that needs refreshing, and a walkthrough given up on is a
 * walkthrough whose photos have nowhere to go.
 */
/**
 * Whether the server refused because something this walkthrough POINTS AT is
 * gone, rather than because of the walkthrough itself.
 */
function isMissingReference(error: unknown): boolean {
  const { status } = (error ?? {}) as { status?: number };
  return status === 404;
}

function isRefusal(error: unknown): boolean {
  const { status, code } = (error ?? {}) as { status?: number; code?: string };
  if (typeof status !== 'number') return false;
  // The two 403s that ARE decisions, named by code exactly as the capture
  // queue names its one. A company that changed plan or switched Magic Quote
  // off mid-walk will refuse this walkthrough on every attempt for the rest of
  // time, and a row that retries forever in silence is the thing `lastError`
  // and the message beneath it were added to prevent.
  if (status === 403 && (code === 'PLAN_REQUIRED' || code === 'FORBIDDEN')) return true;
  // Everything else at 401/403 is a session or a token to refresh, not an
  // answer about this walkthrough — and a walkthrough given up on is a
  // walkthrough whose photographs have nowhere left to go.
  if (status === 401 || status === 403 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}
